import { useMemo, useCallback, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { omit } from 'lodash';
import { useTranslation } from 'react-i18next';

import { useInsightsParams } from './useInsightsParams';
import { useGroupParams } from './useGroupParams';
import { useNotification } from './useNotification';
import { INSIGHTS_QUERY_KEY } from '@insights_module/constants/queryKeys';
import { getShowsQueryObject } from '@insights_module/utils/showsUtils';
import { fetchCSVExport, initiateCSVExport as initiateCSVExportService } from '@services/index';
import { download } from '@utils/download';
import { tokenToObject } from '@utils/commonUtils';
import { getEpisodesQueryObject } from '@insights_module/utils/episodesUtils';
import { useInsightsContext } from '@insights_module/context/InsightsContext';
import { FEATURE_FLAG, useFeatureFlag } from '@auth_module/hooks/useFeatureFlag';

export type CSVExportType =
  | 'groups.garm.csv'
  | 'groups.contextual.csv'
  | 'groups.summary.csv'
  | 'audios.garm.csv'
  | 'audios.contextual.csv'
  | 'audios.summary.csv'
  | 'audios.grouped_by_group.csv';
type CSVExportStatus = 'in_progress' | 'success' | 'failure';

export const CSV_EXPORT_TYPE = {
  GROUPS_GARM: 'groups.garm.csv' as CSVExportType,
  GROUPS_CONTEXTUAL: 'groups.contextual.csv' as CSVExportType,
  GROUPS_SUMMARY: 'groups.summary.csv' as CSVExportType,
  AUDIOS_GARM: 'audios.garm.csv' as CSVExportType,
  AUDIOS_CONTEXTUAL: 'audios.contextual.csv' as CSVExportType,
  AUDIOS_SUMMARY: 'audios.summary.csv' as CSVExportType,
  AUDIOS_GROUPED_BY_GROUP: 'audios.grouped_by_group.csv' as CSVExportType,
};

export const CSV_EXPORT_STATUS = {
  IN_PROGRESS: 'in_progress' as CSVExportStatus,
  SUCCESS: 'success' as CSVExportStatus,
  FAILURE: 'failure' as CSVExportStatus,
};

export const AVAILABLE_AUDIO_EXTERNAL_IDS = {
  MEGAPHONE: 'megaphone',
  MEGAPHONE_PODCAST_ID: 'megaphone_podcast_id',
  GUID: 'guid',
  FEED_EPISODE_GUID: 'feed_episode_guid',
  FEED_URL: 'feed_url',
};

const getRefetchInterval = (refetchAttempt: number) => {
  const baseInterval = 1000;
  const interval = baseInterval * refetchAttempt;
  return interval > 10000 ? 10000 : interval;
};

export const useCSVExport = (exportType: CSVExportType, withDownload = true) => {
  const { isEnabled: isMegaphoneIntegrationEnabled } = useFeatureFlag(
    FEATURE_FLAG.MEGAPHONE_INTEGRATION
  );
  const [isEnabled, setIsEnabled] = useState(false);
  const [shouldRefetch, setShouldRefetch] = useState(false);
  const [refetchAttempt, setRefetchAttempt] = useState(0);
  const [customQueryObject, setCustomQueryObject] = useState<object | null>(null);
  const [onExportSuccess, setOnExportSuccess] = useState<(() => void) | null>(null);

  const { t } = useTranslation();
  const { selectedTableType, selectedShow } = useInsightsContext();
  const { notify, NTFC_TYPE } = useNotification();

  const { queryToken } = useInsightsParams();
  const { groupParams } = useGroupParams();

  const usedGroupParams = useMemo(
    () => omit(groupParams, 'include_named_entities', 'exclude_named_entities'),
    [groupParams]
  );

  const showsQueryObject = getShowsQueryObject(
    queryToken
      ? { ...tokenToObject(queryToken as string), global_search: usedGroupParams?.name || '' }
      : { global_search: usedGroupParams?.name || '' }
  );

  const episodesQueryObject = getEpisodesQueryObject(
    selectedShow || null,
    queryToken
      ? { ...tokenToObject(queryToken as string), global_search: usedGroupParams?.name || '' }
      : { global_search: usedGroupParams?.name || '' }
  );

  const queryObject =
    selectedTableType === 'shows' && !selectedShow
      ? showsQueryObject
      : { query: episodesQueryObject };

  // for audio summary we can specify list of external ids we want to include in the report,
  // also guid and feed_episode_guid are mutually exclusive and BE will return only one that is relevant to the user
  const externalIds =
    exportType === CSV_EXPORT_TYPE.AUDIOS_SUMMARY
      ? isMegaphoneIntegrationEnabled
        ? {
            external_ids: [
              AVAILABLE_AUDIO_EXTERNAL_IDS.MEGAPHONE,
              AVAILABLE_AUDIO_EXTERNAL_IDS.MEGAPHONE_PODCAST_ID,
            ],
          }
        : {
            external_ids: [
              AVAILABLE_AUDIO_EXTERNAL_IDS.GUID,
              AVAILABLE_AUDIO_EXTERNAL_IDS.FEED_EPISODE_GUID,
              AVAILABLE_AUDIO_EXTERNAL_IDS.FEED_URL,
            ],
          }
      : {};

  const { data, isLoading } = useQuery({
    queryKey: [
      INSIGHTS_QUERY_KEY.export_csv,
      customQueryObject,
      queryObject,
      exportType,
      selectedShow,
    ],
    queryFn: () =>
      initiateCSVExportService({
        export_type: exportType,
        query: customQueryObject ? customQueryObject : { ...queryObject.query },
        ...externalIds,
      }),
    enabled: isEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    onError: () => {
      setIsEnabled(false);
      notify({
        type: NTFC_TYPE.DANGER,
        message: t('insights.filters_menu.export_error_message.no_permission'),
      });
    },
  });

  const exportId = useMemo(() => data?.data?.id as string, [data]);

  const {
    data: exportData,
    isLoading: exportDataIsLoading,
    error: exportDataError,
  } = useQuery({
    queryKey: [INSIGHTS_QUERY_KEY.export_csv, exportId],
    queryFn: () => fetchCSVExport(exportId),
    enabled: isEnabled && !!exportId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    onSuccess: (data) => {
      if (data?.data?.status === CSV_EXPORT_STATUS.IN_PROGRESS) {
        setShouldRefetch(true);
        setRefetchAttempt(refetchAttempt + 1);
        return;
      }

      if (data?.data?.status === CSV_EXPORT_STATUS.FAILURE) {
        setIsEnabled(false);
        notify({
          type: NTFC_TYPE.DANGER,
          message: t('insights.filters_menu.export_error_message.failed'),
        });
      }

      if (data?.data?.status === CSV_EXPORT_STATUS.SUCCESS && !withDownload) {
        setIsEnabled(false);
      }

      setShouldRefetch(false);
      setRefetchAttempt(0);
    },
    refetchInterval: shouldRefetch ? getRefetchInterval(refetchAttempt) : false,
  });

  const initiateCSVExport = useCallback((query?: object, onSuccess?: () => void) => {
    query && setCustomQueryObject(query);
    onSuccess && setOnExportSuccess(() => onSuccess);
    setIsEnabled(true);
  }, []);

  const getCSVExport = useCallback(
    () => ({ exportData: exportData?.data, isLoading: exportDataIsLoading || isLoading }),
    [exportData, exportDataIsLoading, isLoading]
  );

  useEffect(() => {
    if (withDownload && exportData?.data?.status === CSV_EXPORT_STATUS.SUCCESS && isEnabled) {
      download(exportData?.data?.file_path, `${exportType + '-' + Date.now()}.csv`);
      setIsEnabled(false);
      setCustomQueryObject(null);
      onExportSuccess && onExportSuccess();
      setOnExportSuccess(null);
      notify({
        type: NTFC_TYPE.SUCCESS,
        message: 'Export successful',
      });
    }
  }, [exportData, withDownload, isEnabled, exportType, onExportSuccess, notify]);

  useEffect(() => {
    if (exportDataError) {
      setIsEnabled(false);
      setShouldRefetch(false);
      setRefetchAttempt(0);
      setCustomQueryObject(null);
      setOnExportSuccess(null);
      notify({
        type: NTFC_TYPE.DANGER,
        message: t('insights.filters_menu.export_error_message.failed'),
      });
    }
  }, [exportDataError]);

  return { initiateCSVExport, getCSVExport, inProgress: isEnabled };
};
